import { A11yModule } from '@angular/cdk/a11y';
import {
  Component, OnInit, OnDestroy, DestroyRef, inject, ViewChild, ElementRef,
  ChangeDetectionStrategy, ChangeDetectorRef, HostListener
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, interval, of } from 'rxjs';
import { catchError, debounceTime, filter, finalize, map, switchMap } from 'rxjs/operators';
import { RealtimeEvent, RealtimeService } from '../../../core/realtime/realtime.service';
import { NoteAuthorDirectoryService, initialsOf } from '../note-author-directory.service';
import { conversationViewerDocuments, unfiledAttachments } from '../note-documents/note-documents';
import { editNoteDocument } from '../note-documents/edit-note-document';
import { OPEN_THREAD_POLL_MS, carryOverReactions, listActivityKey, threadActivityKey } from '../note-documents/live-thread';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';

import { NoteOrgPickerComponent } from '../note-org-picker/note-org-picker.component';
import { MeetingNotesPanelService } from '../services/meeting-notes-panel.service';
import { MeetingNotesService } from '../services/meeting-notes.service';
import { NoteMentionsService } from '../services/note-mentions.service';
import { noteWriteError } from '../services/note-write-error';
import { FileStorageService } from '../../../file-storage/services/file-storage.service';
import { TimeSheetService } from '../../../time-sheet/services/time-sheet.service';
import { ITimeSheetRequest } from '../../../time-sheet/models/ITimeSheetRequest.interface';
import { CorporateFormsService } from '../../services/corporate-forms.service';
import { TimerContext, TimerStoreService } from '../../../shared/services/timer-store.service';
import { CompaniesService } from '../../../services/companies.service';
import { UserService } from '../../../services/user.service';

import {
  ITaggedNoteListItem,
  INoteActivity,
  IConversationAttachment,
} from '../models/ITaggedNote.interface';
import { IMeetingNote, IInlineAttachment, MeetingNoteConfidentiality } from '../models/IMeetingNote.interface';
import { isConversationSystemMessage } from '../share-activity-message';
import { NoteDateGroup, NoteGroupCollapseState, groupNotesByDate } from '../note-date-groups';
import { ClientTodosService } from '../../client-todos/services/client-todos.service';
import {
  ITodoEditorDialogData,
  TodoEditorDialogComponent,
  TodoEditorDialogResult
} from '../../client-todos/todo-editor-dialog/todo-editor-dialog.component';
import { ITodoAssignee, ITodoList } from '../../client-todos/models/ITodoList.interface';
import { MessageTodoIndex, messageTodoFooter } from '../../client-todos/models/message-todo';
import { openTodoViewDialog } from '../../client-todos/todo-view-dialog/todo-view-dialog.component';
import { MessageTodoChipsComponent } from '../../client-todos/message-todo-chips/message-todo-chips.component';
import { IReaction, IReactionSummary, REACTION_EMOJIS } from '../models/IReaction.interface';
import { CompanyUserDTO } from '../../../models/company/company-user-dto.model';
import { UserVM } from '../../../models/user/user-vm.model';
import { IFileStorageItem } from '../../../file-storage/models/IFileStoragePaginated.interface';
import { DMSFileCategory } from '../../../file-storage/enums/DMSFileCategory.enum';
import { RichTextEditorComponent } from '../rich-text-editor/rich-text-editor.component';
import { ReferenceNumberComponent } from '../../../shared/reference-number/reference-number.component';
import { TimesheetListComponent } from '../../reference-numbers/timesheets-dialog/timesheet-list/timesheet-list.component';
import { IDepartment } from '../../../models/departments&services/IDepartment.interface';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
// DocumentViewerDialogComponent and FileStorageUploadDialogComponent are lazy-loaded on demand
// (see viewDocument / openUploadDialog) so the file-storage parsing cluster (mammoth, xlsx,
// pdf-lib, jszip, ...) stays out of the eager bundle even though this panel is always rendered.
import {
  AttachmentTypeDialogComponent, AttachmentType
} from '../attachment-type-dialog/attachment-type-dialog.component';
import { formAutosave } from 'src/app/shared/form-drafts/form-autosave';
import { AutosaveStatusComponent } from 'src/app/shared/form-drafts/autosave-status.component';

import { formatDistanceToNow, format } from 'date-fns';
import { openInOrgTab, orgTabUrl, withTabOrg } from '../../../shared/navigation/org-tab';

type PanelTab = 'discussion' | 'auto_info' | 'documents' | 'timesheet' | 'details' | 'activity';
type FilterStatus = 'all' | 'active' | 'completed';

@Component({
    selector: 'app-meeting-notes-panel',
    imports: [
        A11yModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatMenuModule,
        NoteOrgPickerComponent,
        RichTextEditorComponent,
        ReferenceNumberComponent,
        TimesheetListComponent,
        AutosaveStatusComponent,
        MessageTodoChipsComponent,
    ],
    templateUrl: './meeting-notes-panel.component.html',
    styleUrls: ['./meeting-notes-panel.component.scss'],
    animations: [
        trigger('slidePanel', [
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'none' })),
            ]),
            transition(':leave', [
                animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(100%)' })),
            ]),
        ]),
        trigger('fadeBackdrop', [
            state('void', style({ opacity: 0 })),
            state('*', style({ opacity: 1 })),
            transition(':enter', animate('200ms ease-out')),
            transition(':leave', animate('150ms ease-in')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeetingNotesPanelComponent implements OnInit, OnDestroy {

  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild(TimesheetListComponent) timesheetList?: TimesheetListComponent;

  // Services
  public readonly panelService = inject(MeetingNotesPanelService);
  private readonly notesService = inject(MeetingNotesService);
  private readonly noteMentions = inject(NoteMentionsService);
  private readonly dmsService = inject(FileStorageService);
  private readonly timeSheetService = inject(TimeSheetService);
  private readonly formsService = inject(CorporateFormsService);
  private readonly timerStore = inject(TimerStoreService);
  private readonly companiesService = inject(CompaniesService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly todosService = inject(ClientTodosService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy = inject(DestroyRef);
  private readonly realtime = inject(RealtimeService);
  private readonly authorDirectory = inject(NoteAuthorDirectoryService);

  // State
  public isPanelOpen = false;
  public selectedNote: ITaggedNoteListItem | null = null;

  /**
   * True when the note's thread is locked to a restricted audience.
   *
   * The full-page view has shown this since confidentiality existed; the panel never did, so the
   * same note read as open here and restricted there. Confidentiality is a property of the note,
   * not of the window it happens to be open in.
   */
  isNoteConfidential(note: ITaggedNoteListItem | null): boolean {
    return note?.confidentiality === MeetingNoteConfidentiality.Confidential;
  }

  /**
   * Raises a client to-do from a message, the same way the full view does.
   *
   * Most notes are read in this panel rather than in the full page, so an action that only exists
   * in the expanded view is an action most people never reach.
   */
  createTodoFromMessage(note: IMeetingNote): void {
    const thread = this.selectedNote;
    if (!thread?.formId) {
      this.snackBar.open('This note is not attached to a client, so a to-do cannot be raised from it.', 'Close', { duration: 5000 });
      return;
    }

    const text = this.plainText(note.notes);
    const author = this.isOwnMessage(note.userID) ? 'you' : this.getUserName(note.userID);
    const details = [
      text,
      '',
      messageTodoFooter(thread.title, author, this.formatDate(note.date), note.id)
    ].join('\n');

    const ref = this.dialog.open<TodoEditorDialogComponent, ITodoEditorDialogData, TodoEditorDialogResult>(
      TodoEditorDialogComponent,
      {
        data: {
          mode: 'create',
          prefill: { title: this.firstLine(text, 80) || thread.title || 'Follow-up', details },
          clientName: thread.clientName,
          assignees: this.todoAssignees,
          userId: this.currentUser?.userId ?? null
        },
        width: '620px',
        maxWidth: '96vw',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        panelClass: 'bc-todo-dialog'
      });

    ref.afterClosed().subscribe(result => {
      if (!result) return;
      this.todosService.createTodoList(thread.formId, result).subscribe({
        next: () => {
          this.snackBar.open(`To-do added to ${thread.clientName || 'the client'}'s task lists.`, 'Close', { duration: 4000 });
          this.loadNoteTodos();
        },
        error: () => this.snackBar.open('The to-do was not saved. Try again.', 'Close', { duration: 5000 })
      });
    });
  }

  /** The client's to-do lists; read only to show, under each message, the to-dos raised from it. */
  private noteTodos: ITodoList[] = [];
  private readonly messageTodoIndex = new MessageTodoIndex();

  todosForMessage(note: IMeetingNote): ITodoList[] {
    return this.messageTodoIndex.forMessage(this.noteTodos, note.id, () => this.plainText(note.notes));
  }

  private loadNoteTodos(): void {
    const formId = this.selectedNote?.formId;
    if (this.noteTodos[0]?.submittedFormId !== formId) {
      this.noteTodos = [];
    }
    if (!formId) return;
    this.todosService.getTodoLists(formId).pipe(
      catchError(() => of([] as ITodoList[])),
      takeUntilDestroyed(this.destroy)
    ).subscribe(todos => {
      // Another client was opened while this was loading; its own load answers for it.
      if (this.selectedNote?.formId !== formId) return;
      this.noteTodos = todos || [];
      this.cdr.markForCheck();
    });
  }

  /** Opens a to-do from its chip under the message, to read and tick off without leaving the panel. */
  viewTodo(todo: ITodoList): void {
    const thread = this.selectedNote;
    if (!thread?.formId) return;
    openTodoViewDialog(this.dialog, {
      formId: thread.formId,
      list: todo,
      clientName: thread.clientName,
      userId: this.currentUser?.userId ?? null
    }, {
      onEdit: list => this.editTodo(list),
      onChanged: () => this.loadNoteTodos()
    });
  }

  /** Opens a to-do in the editor, to change it without leaving the panel. */
  editTodo(todo: ITodoList): void {
    const thread = this.selectedNote;
    if (!thread?.formId) return;

    this.dialog.open<TodoEditorDialogComponent, ITodoEditorDialogData, TodoEditorDialogResult>(
      TodoEditorDialogComponent,
      {
        data: {
          mode: 'edit',
          list: todo,
          clientName: thread.clientName,
          assignees: this.todoAssignees,
          userId: this.currentUser?.userId ?? null
        },
        width: '620px',
        maxWidth: '96vw',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        panelClass: 'bc-todo-dialog'
      }
    ).afterClosed().subscribe(result => {
      if (!result) return;
      this.todosService.updateTodoList(thread.formId, todo.id, result).subscribe({
        next: () => {
          this.snackBar.open('To-do updated.', 'Close', { duration: 3000 });
          this.loadNoteTodos();
        },
        error: () => this.snackBar.open('Changes to the to-do were not saved. Try again.', 'Close', { duration: 5000 })
      });
    });
  }

  private get todoAssignees(): ITodoAssignee[] {
    return (this.users || [])
      .filter(u => !!u.id)
      .map(u => ({ id: u.id!, name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email }));
  }

  /** Message HTML to readable text, through the DOM so entities and mention chips come out as words. */
  private plainText(html: string | null | undefined): string {
    if (!html) return '';
    const el = document.createElement('div');
    el.innerHTML = html;
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /** First sentence-ish chunk, for a to-do title that fits on one line. */
  private firstLine(text: string, max: number): string {
    const line = text.split(/[.!?\n]/)[0].trim() || text;
    return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
  }
  public taggedNotes: ITaggedNoteListItem[] = [];
  public isLoadingList = false;
  public isLoadingThread = false;
  public activeTab: PanelTab = 'discussion';
  public filterStatus: FilterStatus = 'all';
  public searchControl = new FormControl('');
  public messageControl = new FormControl('');
  public reactionEmojis = REACTION_EMOJIS;

  public tabsList = [
    { key: 'discussion' as PanelTab, label: 'Discussion', icon: 'chat_bubble_outline' },
    { key: 'auto_info' as PanelTab, label: 'Auto info', icon: 'smart_toy' },
    { key: 'documents' as PanelTab, label: 'Documents', icon: 'description' },
    { key: 'timesheet' as PanelTab, label: 'Timesheet', icon: 'schedule' },
    { key: 'details' as PanelTab, label: 'Details', icon: 'info_outline' },
    { key: 'activity' as PanelTab, label: 'Activity', icon: 'history' },
  ];

  /** The reference this note and everything filed alongside it share. */
  get noteReferenceNumber(): string | null {
    return this.noteThread?.referenceNumber || this.selectedNote?.referenceNumber || null;
  }

  /** Reference number whose timesheet entries feed the Timesheet tab. */
  get timesheetReferenceNumber(): string | null {
    return this.noteReferenceNumber;
  }

  // User data
  public currentUser!: UserVM;
  public users: CompanyUserDTO[] = [];
  /** Who the reply box may tag on the open note; null falls back to `users`. */
  public mentionUsers: CompanyUserDTO[] | null = null;
  public companies: any[] = [];

  // Thread data
  public noteThread: IMeetingNote | null = null;
  public allNotesFlat: IMeetingNote[] = [];
  public threadParticipantIds: number[] = [];
  public noteCompanyName: string = '';
  public dmsFiles: IFileStorageItem[] = [];
  public threadAttachments: IConversationAttachment[] = [];
  public activities: INoteActivity[] = [];

  // Reply state
  public noteToReply: IMeetingNote | null = null;
  public isReplyEdit = false;
  public mentionedUsers: CompanyUserDTO[] = [];
  private usersLoaded = false;

  // ===== Composer autosave =====
  //
  // What is typed into the message box is kept per conversation until it is sent. Started once
  // per opened note (the silent refresh never restarts it); queued files are not drafted.
  readonly replyAutosave = formAutosave<any>({
    formKey: 'corporate.meeting-note-reply',
    recordKey: () => this.replyDraftNoteId ? `note-${this.replyDraftNoteId}` : null,
    read: () => this.readReplyDraft(),
    write: v => this.writeReplyDraft(v),
    title: () => this.noteThread?.title || this.selectedNote?.title
  });
  /** The conversation the composer draft belongs to; null while none is open. */
  private replyDraftNoteId: number | null = null;
  private replyDraftRunning = false;
  private restoringReply = false;

  private startReplyDraft(): void {
    this.replyDraftRunning = true;
    this.replyAutosave.start();
  }

  /** The message was sent or the reply cancelled: nothing left to keep. */
  private dropReplyDraft(): void {
    if (this.restoringReply || !this.replyDraftRunning) return;
    this.replyDraftRunning = false;
    this.replyAutosave.complete();
  }

  /** After a send or cancel, drafting picks up again with the next thing typed. */
  private resumeReplyDraft(): void {
    if (this.replyDraftRunning || this.restoringReply || !this.replyDraftNoteId) return;
    if (this.readReplyDraft()?.notes) this.startReplyDraft();
  }

  /** Only typed text is kept; an empty box, or an edit left as it was, is not. */
  private readReplyDraft(): any {
    // Null between conversations, so the draft of the one just left is never overwritten.
    if (!this.selectedNote || !this.noteThread || !this.replyDraftNoteId
      || this.selectedNote.rootMeetingNoteId !== this.replyDraftNoteId) return null;
    const idle = { notes: '', replyToId: null as number | null, isReplyEdit: false };
    const notes: string = this.messageControl.value ?? '';
    if (!notes.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()) return idle;
    if (this.isReplyEdit && this.noteToReply && notes === this.noteToReply.notes) return idle;
    return { notes, replyToId: this.noteToReply?.id ?? null, isReplyEdit: this.isReplyEdit && !!this.noteToReply };
  }

  private writeReplyDraft(v: any): void {
    this.restoringReply = true;
    try {
      const node = v?.replyToId ? this.allNotesFlat.find(n => n.id === v.replyToId) ?? null : null;
      if (node && v.isReplyEdit) this.editNote(node);
      else if (node) this.replyToNote(node);
      // A plain message, or its target has since been deleted: keep the text as a message.
      else this.cancelReply();
      this.messageControl.setValue(v?.notes ?? '');
      this.messageControl.markAsDirty();
      this.cdr.markForCheck();
    } finally {
      this.restoringReply = false;
    }
  }

  // Reaction picker
  public activeReactionPickerNoteId: number | null = null;

  // Attachments
  public pendingFiles: File[] = [];

  // Timer state
  public isTimerRunning = false;
  public timerElapsedSeconds = 0;
  public timerBillable = true;
  public timerDescription = '';
  public showTimerDescription = false;

  // Inline department/service picker used when the note has no department/service of its own.
  public timerDepartments: IDepartment[] = [];
  public timerDepartmentId: number | null = null;
  public timerServiceId: number | null = null;
  private timerStartTime: Date | null = null;
  private timerInterval: any = null;
  private formCustomerInfo: { businessId?: number; individualId?: number; formType?: number } | null = null;

  // ============================================================
  // Lifecycle
  // ============================================================

  ngOnInit(): void {
    // Deactivated authors are missing from `users`; name them from the full directory.
    this.authorDirectory.ensureLoaded().pipe(takeUntilDestroyed(this.destroy))
      .subscribe(() => this.cdr.markForCheck());
    this.watchTimerStore();
    this.watchOpenThread();

    this.panelService.panelOpen$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(open => {
      this.isPanelOpen = open;
      if (open && !this.usersLoaded) {
        this.loadUserData();
      }
      this.cdr.markForCheck();
    });

    this.panelService.taggedNotes$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(notes => {
      this.taggedNotes = notes;
      this.cdr.markForCheck();
    });

    this.panelService.selectedNote$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(note => {
      // Leaving a conversation: its composer draft is saved under that conversation, not the next.
      if (note?.rootMeetingNoteId !== this.replyDraftNoteId) {
        this.replyAutosave.flush();
        this.replyDraftNoteId = null;
        this.replyDraftRunning = false;
      }
      this.selectedNote = note;
      if (note) {
        this.loadNoteThread(note);
        this.loadMentionUsers(note);
        this.loadNoteTodos();
        this.loadFormCustomerInfo(note.formId);
        this.syncTimerToSelectedNote();
      } else {
        this.resetThreadState();
      }
      this.cdr.markForCheck();
    });

    this.panelService.loadingList$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(loading => {
      this.isLoadingList = loading;
      this.cdr.markForCheck();
    });

    this.messageControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.resumeReplyDraft());

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroy)
    ).subscribe(search => {
      this.panelService.loadTaggedNotes({
        search: search || undefined,
        status: this.filterStatus === 'all' ? undefined : this.filterStatus,
      });
    });
  }

  ngOnDestroy(): void {
    this.panelService.closePanel();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  // ============================================================
  // User Data
  // ============================================================

  private loadUserData(): void {
    forkJoin({
      currentUser: this.userService.getCurrentUser(false).pipe(
        catchError(err => { console.error('[MeetingNotesPanel] Error loading current user:', err); return of(null); })
      ),
      orgUsers: this.companiesService.getOrganizationUsers().pipe(
        catchError(err => { console.error('[MeetingNotesPanel] Error loading org users:', err); return of(null); })
      ),
      orgCompanies: this.companiesService.checkOrganizationCompanies().pipe(
        catchError(() => of(null))
      ),
    }).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe((data: any) => {
      if (data.currentUser) {
        this.currentUser = data.currentUser;
      }
      if (data.orgUsers) {
        this.users = Array.isArray(data.orgUsers) ? data.orgUsers : data.orgUsers?.users || [];
        this.usersLoaded = true;
      }
      if (data.orgCompanies) {
        this.companies = Array.isArray(data.orgCompanies) ? data.orgCompanies : data.orgCompanies || [];
      }
      this.cdr.markForCheck();
    });

    // Load company name for Details tab
    this.loadCompanyName();
  }

  private loadCompanyName(): void {
    const companyId = parseInt(sessionStorage.getItem('companyId') || '0', 10);
    if (companyId > 0) {
      this.companiesService.getCompany(companyId).pipe(
        takeUntilDestroyed(this.destroy),
        catchError(() => of(null))
      ).subscribe((company: any) => {
        if (company) {
          this.noteCompanyName = company.name || company.companyName || '';
          this.cdr.markForCheck();
        }
      });
    }
  }

  // ============================================================
  // Panel Actions
  // ============================================================

  closePanel(): void {
    this.panelService.closePanel();
  }

  selectNote(note: ITaggedNoteListItem): void {
    this.activeTab = 'discussion';
    this.panelService.selectNote(note);
  }

  goBackToList(): void {
    this.panelService.deselectNote();
  }

  setActiveTab(tab: PanelTab): void {
    this.activeTab = tab;
    if (tab === 'documents' && this.selectedNote) {
      this.loadDocuments();
    }
    if (tab === 'activity' && this.selectedNote) {
      this.loadActivity();
    }
  }

  setFilter(status: string): void {
    this.filterStatus = status as FilterStatus;
    this.panelService.loadTaggedNotes({
      search: this.searchControl.value || undefined,
      status: status === 'all' ? undefined : status as any,
    });
  }

  openAllNotesPage(): void {
    window.open(withTabOrg('/tagged-notes'), '_blank');
  }

  openFullView(): void {
    if (!this.selectedNote) return;
    this.panelService.closePanel();
    this.router.navigate(['/tagged-notes'], {
      queryParams: {
        noteId: this.selectedNote.rootMeetingNoteId,
        formId: this.selectedNote.formId,
      }
    });
  }

  openFullViewNewTab(): void {
    if (!this.selectedNote) return;
    openInOrgTab(this.router, '/tagged-notes',
      { noteId: this.selectedNote.rootMeetingNoteId, formId: this.selectedNote.formId }, null);
  }

  copyNoteUrl(): void {
    if (!this.selectedNote) return;
    // No ?org=: a note opens in whichever organization the reader's tab is on (its thread loads under
    // the note's own company), and an ?org= made a tab that was on another organization switch while
    // it loaded — which sent the link to Home instead of the note.
    const url = window.location.origin + orgTabUrl(this.router, '/tagged-notes',
      { noteId: this.selectedNote.rootMeetingNoteId, formId: this.selectedNote.formId }, null);
    navigator.clipboard.writeText(url).then(
      () => this.snackBar.open('Link copied to clipboard', 'Close', { duration: 2000 }),
      () => this.snackBar.open('Failed to copy link', 'Close', { duration: 3000 }),
    );
  }

  openReferencePage(note: ITaggedNoteListItem): void {
    if (note.referenceNumber) {
      window.open(withTabOrg(`/reference-numbers?search=${encodeURIComponent(note.referenceNumber)}`), '_blank');
    }
  }

  /** The client opens in the organisation this tab is on, not the note's (as on the Notes page). */
  openClientPage(note: ITaggedNoteListItem): void {
    if (note.formId) {
      openInOrgTab(this.router, ['/edit-form', note.formId], null, null);
    }
  }

  // ============================================================
  // Thread Loading
  // ============================================================

  /**
   * Loads the conversation. `silent` refreshes the one already open: the messages stay on screen,
   * the view follows the conversation down only if the reader was at its foot, and reactions are
   * fetched only for messages that are new.
   */
  private loadNoteThread(note: ITaggedNoteListItem, options: { silent?: boolean } = {}): void {
    const silent = !!options.silent && !!this.noteThread;
    const followToBottom = !silent || this.isAtBottom();
    const previous = this.allNotesFlat;

    if (!silent) {
      this.isLoadingThread = true;
      this.cdr.markForCheck();
    }

    this.panelService.getNoteThread(note.formId, note.rootMeetingNoteId, note.companyId).pipe(
      catchError(err => {
        if (!silent) {
          console.error('Error loading note thread:', err);
          this.snackBar.open('Error loading conversation', 'Close', { duration: 3000 });
        }
        return of(null);
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe(thread => {
      // The reader may have moved to another note while a refresh was in flight.
      if (silent && this.selectedNote?.rootMeetingNoteId !== note.rootMeetingNoteId) return;

      if (thread) {
        this.noteThread = thread;
        // Only when the thread that just loaded is the one being timed: otherwise the label and
        // reference of the note on screen would overwrite those of the note the timer belongs to.
        if (this.isTimerRunning && this.timerOwner?.key === this.keyFor(note)) {
          this.timerOwner = {
            ...this.timerOwner,
            label: thread.title || this.timerOwner.label,
            reference: thread.referenceNumber || this.timerOwner.reference,
          };
          this.persistTimer();
        }
        this.flattenNotes([thread]);
        this.loadReactionsForNotes(silent ? carryOverReactions(previous, this.allNotesFlat) : this.allNotesFlat);
        if (followToBottom) setTimeout(() => this.scrollToBottom(), 100);

        // Once per opened conversation: a refresh, silent or not, never restores a second time.
        if (this.selectedNote?.rootMeetingNoteId === note.rootMeetingNoteId
          && this.replyDraftNoteId !== note.rootMeetingNoteId) {
          this.replyDraftNoteId = note.rootMeetingNoteId;
          this.startReplyDraft();
        }
      }
      if (!silent) this.isLoadingThread = false;
      this.cdr.markForCheck();
    });
  }

  // ===== Live conversation =====
  //
  // Replies from other people used to appear only after reopening the note: the list was live, the
  // open conversation was not. Same arrangement as the tagged-notes page.

  /** Writes this tab has in flight; a push arriving mid-write is held until they finish. */
  private localWrites = 0;
  private refreshHeldForWrite = false;

  /** Note and list-row activity last compared with the screen, so a row is acted on once per change. */
  private lastSeenRowKey: string | null = null;

  private beginLocalWrite(): void {
    this.localWrites++;
  }

  private endLocalWrite(): void {
    this.localWrites = Math.max(0, this.localWrites - 1);
    if (this.localWrites === 0 && this.refreshHeldForWrite) {
      this.refreshHeldForWrite = false;
      this.refreshOpenThread();
    }
  }

  private refreshOpenThread(): void {
    const note = this.selectedNote;
    if (!note || !this.noteThread || this.isLoadingThread) return;
    if (this.localWrites > 0) {
      this.refreshHeldForWrite = true;
      return;
    }
    this.loadNoteThread(note, { silent: true });
  }

  /**
   * A push refreshes the open conversation at once; a list row showing more or newer messages than
   * the screen refetches it; and a note no push can reach (hub down, or another organization's) has
   * its delta checked every few seconds while it is open.
   */
  private watchOpenThread(): void {
    this.realtime.on(RealtimeEvent.NotesChanged).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.refreshOpenThread());

    this.panelService.taggedNotes$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(notes => {
      const note = this.selectedNote;
      const thread = this.noteThread;
      if (!note || !thread?.id) return;

      const row = notes.find(n => n.rootMeetingNoteId === note.rootMeetingNoteId);
      if (!row) return;

      const rowKey = `${note.rootMeetingNoteId}:${listActivityKey(row)}`;
      if (rowKey === this.lastSeenRowKey) return;
      this.lastSeenRowKey = rowKey;

      if (listActivityKey(row) !== threadActivityKey(this.allNotesFlat, thread.id)) this.refreshOpenThread();
    });

    interval(OPEN_THREAD_POLL_MS).pipe(
      filter(() => this.isPanelOpen && !!this.selectedNote && !!this.noteThread
        && document.visibilityState === 'visible'),
      filter(() => !this.realtime.isConnected
        || this.noteThread!.companyId !== this.companiesService.managedCompanyId),
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.panelService.pollUpdatesNow());
  }

  /** Whether the conversation is sitting at its foot, within a line's slack. */
  private isAtBottom(): boolean {
    const el = this.chatContainer?.nativeElement as HTMLElement | undefined;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  private resetThreadState(): void {
    this.noteThread = null;
    this.allNotesFlat = [];
    this.dmsFiles = [];
    this.threadAttachments = [];
    this.activities = [];
    this.noteToReply = null;
    this.isReplyEdit = false;
    this.pendingFiles = [];
    this.mentionedUsers = [];
    this.activeReactionPickerNoteId = null;
  }

  // ============================================================
  // Discussion / Replies
  // ============================================================

  replyToNote(node: IMeetingNote): void {
    this.cancelReply();
    this.noteToReply = node;
    this.messageControl.setValue('');
  }

  editNote(node: IMeetingNote): void {
    this.cancelReply();
    this.isReplyEdit = true;
    this.noteToReply = node;
    this.messageControl.setValue(node.notes);
  }

  cancelReply(): void {
    this.dropReplyDraft();
    this.noteToReply = null;
    this.isReplyEdit = false;
    this.messageControl.setValue('');
    this.pendingFiles = [];
    this.mentionedUsers = [];
  }

  /**
   * Attachments on their own are a message. An edit still needs text — an edit that blanked the
   * body would wipe the message it is editing. Without this, files queued in the composer (a company
   * document included, already filed in Documents) could not be sent until something was typed, and
   * were easy to leave behind.
   */
  get canSendMessage(): boolean {
    if (this.isSendingMessage) return false;
    const hasText = !!this.messageControl.value?.trim();
    return hasText || (!this.isReplyEdit && this.pendingFiles.length > 0);
  }

  /** True while a message is being posted, so one press is one message however long the upload takes. */
  isSendingMessage = false;

  /**
   * Whether a message has body text. Attachment-only messages have none, and an empty bubble
   * renders as a blank block above the attachment cards.
   */
  hasMessageBody(note: IMeetingNote): boolean {
    return !!note.notes?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  }

  submitMessage(): void {
    const notesValue = this.messageControl.value ?? '';
    const hasText = notesValue.trim() !== '';
    if (this.isSendingMessage) return;
    if (!hasText && this.pendingFiles.length === 0) return;
    if (!this.selectedNote || !this.noteThread) return;

    if (this.isReplyEdit && this.noteToReply) {
      if (!hasText) return;
      const noteToUpdate = {
        ...this.noteToReply,
        notes: notesValue,
        userID: this.currentUser.userId,
      };

      this.beginLocalWrite();
      this.notesService.editNote(this.selectedNote.formId, noteToUpdate).pipe(
        takeUntilDestroyed(this.destroy),
        finalize(() => this.endLocalWrite())
      ).subscribe(() => {
        this.dropReplyDraft();
        this.updateNoteInTree(this.noteToReply!.id!, notesValue);
        this.flattenNotes([this.noteThread!]);
        this.cancelReply();
        this.snackBar.open('Message updated', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      });
    } else {
      const parentId = this.noteToReply?.id || this.noteThread.id;
      // Claimed before the post rather than after it: the DMS upload drawer posts whatever is still
      // queued in the composer as its own message when it closes.
      const filesToSend = this.pendingFiles;
      this.pendingFiles = [];

      const newNote: IMeetingNote = {
        referenceNumber: this.noteThread.referenceNumber,
        title: this.noteThread.title,
        year: this.noteThread.year,
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
        departmentId: this.noteThread.departmentId,
        serviceId: this.noteThread.serviceId,
        userID: this.currentUser.userId,
        notes: notesValue,
        parentMeetingNoteId: parentId!,
      };

      // The message is already posted by the time its files go up, so a failed upload cannot undo
      // it — but it used to be swallowed: the message arrived without its files while the composer
      // said "Message sent", and the files were gone from the composer too.
      let attachFailed = false;

      this.isSendingMessage = true;
      this.beginLocalWrite();
      this.notesService.createNote(this.selectedNote.formId, newNote).pipe(
        switchMap((id) => {
          newNote.id = id;
          newNote.replies = [];
          newNote.inlineAttachments = [];

          if (filesToSend.length > 0) {
            return this.panelService.uploadInlineAttachments(
              this.selectedNote!.formId,
              id,
              filesToSend,
            ).pipe(
              map((attachments) => { newNote.inlineAttachments = attachments; return id; }),
              catchError(() => { attachFailed = true; return of(id); })
            );
          }
          return of(id);
        }),
        catchError((err) => {
          // Nothing was posted, so hand the files back rather than swallowing them with the message.
          this.pendingFiles = [...filesToSend, ...this.pendingFiles];
          this.snackBar.open(noteWriteError(err, 'Could not send the message'), 'Close', { duration: 6000 });
          return of(null);
        }),
        takeUntilDestroyed(this.destroy),
        finalize(() => this.endLocalWrite())
      ).subscribe((created) => {
        this.isSendingMessage = false;
        if (created === null) {
          this.cdr.markForCheck();
          return;
        }
        this.dropReplyDraft();
        this.addReplyToTree(parentId!, newNote);
        this.flattenNotes([this.noteThread!]);
        this.cancelReply();
        if (attachFailed) {
          // Handed back rather than lost: Send posts them on their own.
          this.pendingFiles = [...filesToSend, ...this.pendingFiles];
          this.snackBar.open(
            `Message sent, but the ${filesToSend.length > 1 ? 'files' : 'file'} could not be attached. `
              + 'They are still in the composer — press Send to try again.',
            'Close', { duration: 8000 });
        } else {
          this.snackBar.open('Message sent', 'Close', { duration: 2000 });
        }
        this.cdr.markForCheck();
        setTimeout(() => this.scrollToBottom(), 100);
      });
    }
  }

  deleteNote(node: IMeetingNote): void {
    if (!this.selectedNote) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Message',
        text: 'Are you sure you want to delete this message? Any replies to it will also be deleted.',
        subText: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.beginLocalWrite();
      this.notesService.deleteNote(this.selectedNote!.formId, node.id!).pipe(
        takeUntilDestroyed(this.destroy),
        finalize(() => this.endLocalWrite())
      ).subscribe(() => {
        this.deleteNoteFromTree(node);
        this.flattenNotes([this.noteThread!]);
        this.snackBar.open('Message deleted', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      });
    });
  }

  onUserMentioned(user: CompanyUserDTO): void {
    if (!this.mentionedUsers.find(u => u.id === user.id)) {
      this.mentionedUsers.push(user);
    }
    const note = this.selectedNote;
    this.noteMentions.warnIfOutsideAudience(user, note?.formId, note?.rootMeetingNoteId);
  }

  /**
   * The people the reply box can tag: the note's organisation and those holding its client. Null
   * (still loading, or unavailable) falls back to `users`; the API refuses a tag outside them.
   */
  private loadMentionUsers(note: ITaggedNoteListItem): void {
    this.mentionUsers = null;
    this.noteMentions.forNote(note.formId, note.rootMeetingNoteId, note.companyId).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(users => {
      if (this.selectedNote?.rootMeetingNoteId !== note.rootMeetingNoteId) return;
      this.mentionUsers = users;
      this.cdr.markForCheck();
    });
  }

  // ============================================================
  // Inline Attachments
  // ============================================================

  deleteInlineAttachment(note: IMeetingNote, attachment: IInlineAttachment): void {
    if (!this.selectedNote) return;

    this.panelService.deleteInlineAttachment(
      this.selectedNote.formId,
      note.id!,
      attachment.id
    ).pipe(
      takeUntilDestroyed(this.destroy),
      catchError(() => {
        this.snackBar.open('Error deleting attachment', 'Close', { duration: 3000 });
        return of(null);
      })
    ).subscribe(res => {
      if (res !== null) {
        note.inlineAttachments = (note.inlineAttachments || []).filter(a => a.id !== attachment.id);
        this.snackBar.open('Attachment deleted', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      }
    });
  }

  downloadInlineAttachment(attachment: IInlineAttachment): void {
    if (attachment.blobUrl) {
      const a = document.createElement('a');
      a.href = attachment.blobUrl;
      a.download = attachment.name;
      a.target = '_blank';
      a.click();
    }
  }

  isImageAttachment(attachment: IInlineAttachment): boolean {
    const ext = attachment.name?.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext || '');
  }

  // ============================================================
  // File Attachments
  // ============================================================

  triggerFileUpload(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files: File[] = Array.from(input.files);
      this.showAttachmentTypeChoice(files);
    }
    input.value = '';
  }

  private showAttachmentTypeChoice(files: File[]): void {
    const dialogRef = this.dialog.open(AttachmentTypeDialogComponent, {
      width: '480px',
      maxWidth: '96vw',
      data: { fileNames: files.map(f => f.name) },
    });

    dialogRef.afterClosed().subscribe(async (result: AttachmentType | undefined) => {
      if (!result) return;

      if (result === 'screenshot') {
        this.pendingFiles = [...this.pendingFiles, ...files];
        this.cdr.markForCheck();
      } else if (result === 'company_document') {
        // Add files as inline attachments
        this.pendingFiles = [...this.pendingFiles, ...files];
        this.cdr.markForCheck();

        // Also open DMS upload dialog for proper categorization
        if (this.selectedNote && this.noteThread) {
          const {FileStorageUploadDialogComponent} = await import('../../../file-storage/components/file-storage-upload-dialog/file-storage-upload-dialog.component');
          const uploadDialogRef = FileStorageUploadDialogComponent.openDrawer(this.dialog, {
            submittedFormId: this.selectedNote.formId,
            acr: 'NOTE',
            code: 'MN',
            returnFile: true,
            description: 'Notes',
            client: this.selectedNote.clientName || '',
            referenceNumber: this.noteThread?.referenceNumber || this.selectedNote.referenceNumber || null,
            preSelectedFiles: files,
            // Linkage for the "Select Existing" flow so a linked document shows in this note's Documents tab.
            sourceDocumentId: this.noteThread!.id!.toString(),
            sourceDocumentType: 0,
            category: DMSFileCategory.Notes,
            meetingNoteId: this.noteThread!.id,
          });

          uploadDialogRef.afterClosed().pipe(
            switchMap(res => {
              if (res?.linkedExisting) return of({ documents: (res.documents || []) as IFileStorageItem[], uploaded: false });
              if (!res || !res.file) return of(null);
              const uploadFiles = Array.isArray(res.file) ? res.file : [res];
              const uploads = uploadFiles.map((f: any) => {
                const entry = {
                  ...res,
                  category: DMSFileCategory.Notes,
                  sourceDocumentId: this.noteThread!.id!.toString(),
                  meetingNoteId: this.noteThread!.id,
                  client: this.selectedNote!.clientName || '',
                  file: f.file || f,
                };
                return this.dmsService.uploadFile(entry, this.selectedNote!.formId);
              });
              return forkJoin<IFileStorageItem[]>(uploads).pipe(
                map(documents => ({ documents, uploaded: true })),
                catchError(() => {
                  // Used to fail without a word. The files are still queued, so Send can at least
                  // put them in the conversation.
                  this.snackBar.open(
                    'Could not file the document in Documents. It is still in the composer — press Send to post it to the conversation.',
                    'Close', { duration: 8000 });
                  return of(null);
                }));
            })
          ).subscribe(result => {
            if (!result) return;
            this.dmsFiles = [...this.dmsFiles, ...result.documents];
            this.snackBar.open('Document uploaded to DMS', 'Close', { duration: 3000 });
            this.cdr.markForCheck();
            // The files were picked from the chat composer, so post them to the conversation too —
            // the upload is what the user meant by "attach", not a draft waiting for a caption. This
            // panel used to leave them queued, and a composer that would not send without text meant
            // they often never reached the conversation at all.
            if (result.uploaded) this.postFilesToChat(files);
          });
        }
      }
    });
  }

  /**
   * Post files to the thread as their own message, with no body text. Used once a DMS upload
   * started from the composer finishes. Anything else the user has queued in the composer, and
   * whatever they are typing, is left alone.
   */
  private postFilesToChat(files: File[]): void {
    if (!this.selectedNote || !this.noteThread) return;

    // Only what is still queued in the composer. Pressing Send while the DMS dialog was open sends
    // these files with that message, and posting them again would put them in the thread twice.
    const queued = files.filter(file => this.pendingFiles.includes(file));
    if (!queued.length) return;

    const formId = this.selectedNote.formId;
    const parentId = this.noteToReply?.id || this.noteThread.id;
    const newNote: IMeetingNote = {
      referenceNumber: this.noteThread.referenceNumber,
      title: this.noteThread.title,
      year: this.noteThread.year,
      date: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      departmentId: this.noteThread.departmentId,
      serviceId: this.noteThread.serviceId,
      userID: this.currentUser.userId,
      notes: '',
      parentMeetingNoteId: parentId!,
    };

    this.beginLocalWrite();
    this.notesService.createNote(formId, newNote).pipe(
      switchMap((id) => {
        newNote.id = id;
        newNote.replies = [];
        newNote.inlineAttachments = [];
        return this.panelService.uploadInlineAttachments(formId, id, queued).pipe(
          map((attachments): number | null => { newNote.inlineAttachments = attachments; return id; }),
          // This message has no text, so without its files it would be an empty row in the thread.
          catchError(() => this.notesService.deleteNote(formId, id).pipe(
            catchError(() => of(null)),
            map(() => null))),
        );
      }),
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroy),
      finalize(() => this.endLocalWrite())
    ).subscribe((created) => {
      // Filed either way, so they leave the composer: sending them again would post a second copy.
      this.pendingFiles = this.pendingFiles.filter(f => !queued.includes(f));
      if (created === null) {
        this.snackBar.open(
          'The document is filed in Documents, but could not be attached to a message.',
          'Close', { duration: 6000 });
      } else {
        this.addReplyToTree(parentId!, newNote);
        this.flattenNotes([this.noteThread!]);
        setTimeout(() => this.scrollToBottom(), 100);
      }
      this.cdr.markForCheck();
    });
  }

  removePendingFile(index: number): void {
    this.pendingFiles.splice(index, 1);
    this.cdr.markForCheck();
  }

  /**
   * Pick existing DMS document(s) and attach them to the message being composed (as inline
   * attachments, so they appear in the chat). The blob is fetched and reused — no re-upload prompt.
   */
  async attachExistingToChat(): Promise<void> {
    // On demand, like the upload dialog below: this panel is part of every page's first bundle.
    const {DocumentPickerDialogComponent} = await import('../../../file-storage/components/document-picker-dialog/document-picker-dialog.component');
    const dialogRef = this.dialog.open(DocumentPickerDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { client: this.selectedNote?.clientName || '', multiSelect: true }
    });

    dialogRef.afterClosed().subscribe((docs: IFileStorageItem[] | undefined) => {
      if (!docs?.length) return;
      forkJoin(
        docs.map(d => this.dmsService.getDocumentForViewing(d.id).pipe(
          map(blob => new File([blob], d.originalName, { type: d.contentType || blob.type })),
          catchError(() => of(null))
        ))
      ).subscribe(files => {
        const valid = files.filter((f): f is File => !!f);
        if (valid.length) {
          this.pendingFiles = [...this.pendingFiles, ...valid];
          this.snackBar.open(`Attached ${valid.length} document${valid.length > 1 ? 's' : ''} to the message`, 'Close', { duration: 2500 });
          this.cdr.markForCheck();
        }
      });
    });
  }

  async openUploadDialog(selectExisting = false): Promise<void> {
    if (!this.selectedNote || !this.noteThread) return;

    const {FileStorageUploadDialogComponent} = await import('../../../file-storage/components/file-storage-upload-dialog/file-storage-upload-dialog.component');
    const dialogRef = FileStorageUploadDialogComponent.openDrawer(this.dialog, {
      submittedFormId: this.selectedNote.formId,
      acr: 'NOTE',
      code: 'MN',
      returnFile: true,
      description: 'Notes',
      client: this.selectedNote.clientName || '',
      referenceNumber: this.noteThread?.referenceNumber || this.selectedNote.referenceNumber || null,
      // Linkage for the "Select Existing" flow so a linked document shows in this note's Documents tab.
      sourceDocumentId: this.noteThread!.id!.toString(),
      sourceDocumentType: 0,
      category: DMSFileCategory.Notes,
      meetingNoteId: this.noteThread!.id,
      selectExisting,
    });

    dialogRef.afterClosed().pipe(
      switchMap(res => {
        if (res?.linkedExisting) {
          return of(res.documents || []);
        }
        if (!res || !res.file) return of(null);

        const files = Array.isArray(res.file) ? res.file : [res];
        const uploads = files.map((f: any) => {
          const entry = {
            ...res,
            category: DMSFileCategory.Notes,
            sourceDocumentId: this.noteThread!.id!.toString(),
            meetingNoteId: this.noteThread!.id,
            client: this.selectedNote!.clientName || '',
            file: f.file || f,
          };
          return this.dmsService.uploadFile(entry, this.selectedNote!.formId);
        });
        return forkJoin(uploads);
      })
    ).subscribe(result => {
      if (result) {
        const newFiles = Array.isArray(result) ? result : [result];
        this.dmsFiles = [...this.dmsFiles, ...newFiles];
        this.snackBar.open('File uploaded', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      }
    });
  }

  /** Edit an important document's details and status (Draft, Signed, Final, …) in place. */
  editDocument(doc: IFileStorageItem, event?: Event): void {
    event?.stopPropagation();
    editNoteDocument(this.dialog, this.dmsService, doc, this.noteThread?.companyId).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe({
      next: updated => {
        if (!updated) return;
        this.dmsFiles = this.dmsFiles.map(d => d.id === doc.id ? { ...d, ...updated } : d);
        this.snackBar.open('Document updated', 'Close', { duration: 2500 });
        this.cdr.markForCheck();
      },
      error: () => this.snackBar.open('Could not save the document. Try again.', 'Close', { duration: 5000 }),
    });
  }

  /**
   * Files something already posted in the conversation as an important document: the upload drawer
   * with the attachment pre-selected, so it gets a document type like any other filed document.
   * Fetched through the API (the SAS link cannot be read across origins); the DMS gets its own copy
   * and the message keeps its attachment.
   */
  async fileAttachmentAsImportant(noteId: number | undefined, attId: number | undefined, name: string,
                                  contentType?: string | null, event?: Event): Promise<void> {
    event?.stopPropagation();
    const formId = this.selectedNote?.formId;
    if (!formId || !noteId || !attId || !this.noteThread) return;

    const {FileStorageUploadDialogComponent} = await import('../../../file-storage/components/file-storage-upload-dialog/file-storage-upload-dialog.component');
    this.panelService.getInlineAttachmentBlob(formId, noteId, attId, this.noteThread.companyId).pipe(
      switchMap(blob => FileStorageUploadDialogComponent.openDrawer(this.dialog, {
        submittedFormId: formId,
        acr: 'NOTE',
        code: 'MN',
        returnFile: true,
        description: 'Notes',
        client: this.selectedNote?.clientName || '',
        referenceNumber: this.noteThread?.referenceNumber || this.selectedNote?.referenceNumber || null,
        preSelectedFiles: [new File([blob], name, { type: contentType || blob.type })],
        sourceDocumentId: this.noteThread!.id!.toString(),
        sourceDocumentType: 0,
        category: DMSFileCategory.Notes,
        meetingNoteId: this.noteThread!.id,
      }).afterClosed()),
      switchMap(res => {
        if (res?.linkedExisting) return of(res.documents || []);
        if (!res || !res.file) return of(null);
        const files = Array.isArray(res.file) ? res.file : [res];
        return forkJoin(files.map((f: any) => this.dmsService.uploadFile({
          ...res,
          category: DMSFileCategory.Notes,
          sourceDocumentId: this.noteThread!.id!.toString(),
          meetingNoteId: this.noteThread!.id,
          client: this.selectedNote!.clientName || '',
          file: f.file || f,
        }, formId)));
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe({
      next: result => {
        if (!result) return;
        const newFiles = Array.isArray(result) ? result : [result];
        this.dmsFiles = [...this.dmsFiles, ...newFiles];
        this.snackBar.open('Filed as an important document', 'Close', { duration: 3000 });
        this.cdr.markForCheck();
      },
      error: () => this.snackBar.open('Could not file this attachment. Try again.', 'Close', { duration: 5000 }),
    });
  }

  // ============================================================
  // Documents Tab
  // ============================================================

  private loadDocuments(): void {
    if (!this.selectedNote || !this.noteThread) return;

    const searchConfig: any = {
      submittedFormId: this.selectedNote.formId,
      category: DMSFileCategory.Notes,
    };

  /**
   * Documents filed against this note, plus anything sharing its reference number.
   *
   * A note, its planner tasks, its to-dos and its timesheet entries all carry the same reference
   * — that is what the reference is for. So a document uploaded from a to-do belongs to the same
   * piece of work as the note, and looking for it under the note is where people actually go.
   * Previously only files whose sourceDocumentId was this note appeared, so anything attached
   * anywhere else in the same job was invisible here.
   *
   * The second search deliberately carries no category filter: the point is to reach documents
   * filed under Planner, Tasks or straight into the DMS, none of which are category Notes.
   */
    const noteId = this.noteThread.id!.toString();
    const reference = this.noteReferenceNumber;
    const paging = { pageNumber: 1, pageSize: 10000 };

    forkJoin({
      own: this.dmsService.search(searchConfig, paging),
      shared: reference
        ? this.dmsService.search(
            { submittedFormId: this.selectedNote.formId, referenceNumber: reference } as any,
            paging,
          ).pipe(catchError(() => of(null)))
        : of(null),
    }).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(({ own, shared }) => {
      const mine = own.items.filter(f => f.sourceDocumentId === noteId);
      const seen = new Set(mine.map(f => f.id));
      this.dmsFiles = [...mine, ...(shared?.items ?? []).filter(f => !seen.has(f.id))];
      this.cdr.markForCheck();
    });

    // Build conversation attachments from the loaded thread notes
    this.threadAttachments = this.allNotesFlat.reduce((acc, note) => {
      if (note.inlineAttachments?.length) {
        for (const att of note.inlineAttachments) {
          acc.push({
            id: att.id,
            meetingNoteId: note.id!,
            fileName: att.name,
            fileSize: att.fileSize,
            fileType: att.contentType || '',
            uploadedByUserId: note.userID!,
            uploadedDate: att.uploadedAt,
            downloadUrl: att.blobUrl,
          });
        }
      }
      return acc;
    }, [] as IConversationAttachment[]);
    this.cdr.markForCheck();
  }

  async viewDocument(doc: IFileStorageItem): Promise<void> {
    const {DocumentViewerDialogComponent} = await import('../../../file-storage/components/document-viewer-dialog/document-viewer-dialog.component');
    this.dialog.open(DocumentViewerDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '85vh',
      panelClass: 'document-viewer-dialog',
      data: { document: doc },
    });
  }

  /** Preview a chat inline attachment (fetched via the authenticated API, not the SAS URL). */
  /**
   * Conversation attachments that are not already listed under Important Documents.
   *
   * Importing a conversation attachment as a company document creates a DMS record pointing at the
   * same blob, and the two lists are built independently — so the file appeared twice, once with the
   * taxonomy name and once with the original filename.
   *
   * Matched on the blob PATH of the two SAS URLs, ignoring the '?' token: same blob is provably the
   * same file, which name or size matching could not establish. When the import copies the blob
   * rather than referencing it the paths differ and both rows remain; the durable fix for that is a
   * document reference on MeetingNoteInlineAttachment, which does not exist yet.
   */
  get visibleThreadAttachments(): IConversationAttachment[] {
    const dmsBlobPaths = new Set(
      this.dmsFiles
        .map(doc => this.blobPathOf(doc.downloadUrl))
        .filter((path): path is string => !!path)
    );

    const notSameBlob = dmsBlobPaths.size === 0
      ? this.threadAttachments
      : this.threadAttachments.filter(att => {
          const path = this.blobPathOf(att.downloadUrl);
          return !path || !dmsBlobPaths.has(path);
        });

    // A separate copy filed as an important document is listed there only.
    return unfiledAttachments(notSameBlob, this.dmsFiles);
  }

  /** Blob path of a SAS URL, without the token, or null when it is not an absolute URL. */
  private blobPathOf(url?: string): string | null {
    if (!url || !/^https?:\/\//i.test(url)) return null;
    try {
      const parsed = new URL(url);
      return `${parsed.host}${parsed.pathname}`.toLowerCase();
    } catch {
      return null;
    }
  }

  previewInlineAttachment(noteId: number | undefined, att: IInlineAttachment): void {
    this.openAttachmentPreview(noteId, att.id, att.name, att.contentType, att.blobUrl);
  }

  previewConversationAttachment(att: IConversationAttachment): void {
    this.openAttachmentPreview(att.meetingNoteId, att.id, att.fileName, att.fileType, att.downloadUrl);
  }

  private openAttachmentPreview(
    noteId: number | undefined,
    attId: number | undefined,
    name: string,
    contentType?: string | null,
    directUrl?: string,
  ): void {
    const formId = this.selectedNote?.formId;
    if (!formId || !noteId || !attId) return;

    // Prefer the SAS URL the API already returns (MeetingNoteInlineAttachmentVm.BlobUrl, resolved
    // against the file's OWNING company's container). Office documents are rendered by Microsoft's
    // hosted viewer, which fetches the URL server-side — so a blob: object URL built here can never
    // be opened by it, and Word/Excel previews failed every time. The SAS URL is fetchable.
    if (directUrl && /^https?:\/\//i.test(directUrl)) {
      void this.openViewerDialog(String(attId), name, directUrl, contentType, conversationViewerDocuments(this.threadAttachments));
      return;
    }

    // No usable URL: fall back to the authenticated download. Still correct for anything rendered
    // locally (images, PDF, text).
    this.panelService.getInlineAttachmentBlob(formId, noteId, attId, this.noteThread?.companyId).subscribe({
      next: async (blob) => {
        const url = URL.createObjectURL(new Blob([blob], { type: contentType || blob.type }));
        await this.openViewerDialog(String(attId), name, url, contentType);
      },
      error: () => this.snackBar.open('Unable to preview this file', 'Close', { duration: 3000 }),
    });
  }

  /**
   * `siblings` are every file shared in this conversation, so the viewer's next/previous pages
   * through them in the order they were posted, as it does for Important Documents.
   */
  private async openViewerDialog(id: string, name: string, url: string, contentType?: string | null,
                                 siblings: IFileStorageItem[] = []): Promise<void> {
    const {DocumentViewerDialogComponent} = await import('../../../file-storage/components/document-viewer-dialog/document-viewer-dialog.component');
    const document = { id, originalName: name, downloadUrl: url, contentType: contentType || '', acr: '', code: '' } as IFileStorageItem;
    const currentIndex = siblings.findIndex(d => d.id === id);
    this.dialog.open(DocumentViewerDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '85vh',
      panelClass: 'document-viewer-dialog',
      data: currentIndex >= 0
        ? { document, documents: siblings, currentIndex }
        : { document },
    });
  }

  downloadDocument(doc: IFileStorageItem): void {
    const a = document.createElement('a');
    a.href = doc.downloadUrl;
    a.download = doc.generatedFileName;
    a.target = '_blank';
    a.click();
  }

  downloadConversationAttachment(att: IConversationAttachment): void {
    if (att.downloadUrl) {
      const a = document.createElement('a');
      a.href = att.downloadUrl;
      a.download = att.fileName || 'attachment';
      a.target = '_blank';
      a.click();
    }
  }

  // ============================================================
  // Activity Tab
  // ============================================================

  private loadActivity(): void {
    if (!this.selectedNote) return;

    this.panelService.getActivity(this.selectedNote.rootMeetingNoteId).pipe(
      catchError(() => of({ items: [], totalCount: 0, totalPages: 0 })),
      takeUntilDestroyed(this.destroy)
    ).subscribe(res => {
      this.activities = (res.items || []).map((item: any) => this.mapActivity(item));
      this.cdr.markForCheck();
    });
  }

  private mapActivity(item: any): INoteActivity {
    const type = item.type || (item.parentMeetingNoteId ? 'reply' : 'created');
    const rawDetail = item.detail || item.notes || '';
    return {
      id: item.id || item.noteId,
      type,
      userId: item.userId,
      // Audit-sourced entries carry a display name instead of a numeric user id.
      userDisplayName: item.userDisplayName,
      userAzureObjectId: item.userAzureObjectId,
      timestamp: item.timestamp || item.date,
      detail: this.stripHtml(rawDetail),
      metadata: item.metadata,
    };
  }

  // ============================================================
  // Reactions
  // ============================================================

  toggleReactionPicker(node: IMeetingNote): void {
    this.activeReactionPickerNoteId =
      this.activeReactionPickerNoteId === node.id ? null : node.id!;
    this.cdr.markForCheck();
  }

  addReaction(node: IMeetingNote, emojiItem: { emoji: string; code: string; label: string }): void {
    if (!this.selectedNote) return;
    this.activeReactionPickerNoteId = null;

    const existing = node.reactionSummary?.find(
      r => r.emoji === emojiItem.emoji && r.hasCurrentUserReacted
    );
    if (existing) {
      this.removeReaction(node, emojiItem.emoji);
      return;
    }

    const noteOwner = this.users?.find(u => u.azureObjectId === this.currentUser?.azureObjectId);
    const request = {
      emoji: emojiItem.emoji,
      noteOwnerAzureObjectId: noteOwner?.azureObjectId || '',
      clientName: this.selectedNote.clientName,
      noteTitle: this.noteThread?.title || node.title,
      rootMeetingNoteId: this.selectedNote.rootMeetingNoteId,
    };

    this.notesService.addReaction(this.selectedNote.formId, node.id!, request).pipe(
      takeUntilDestroyed(this.destroy),
      catchError(() => of(null))
    ).subscribe(reaction => {
      if (reaction) {
        if (!node.reactions) node.reactions = [];
        node.reactions.push(reaction);
        this.updateNodeReactionSummary(node, emojiItem.emoji, true, reaction.id);
        this.cdr.markForCheck();
      }
    });
  }

  removeReaction(node: IMeetingNote, emoji: string): void {
    if (!this.selectedNote) return;

    const userReaction = node.reactions?.find(
      r => r.userAzureObjectId === this.currentUser?.azureObjectId && r.emoji === emoji
    );
    const reactionId = userReaction?.id ??
      node.reactionSummary?.find(r => r.emoji === emoji && r.hasCurrentUserReacted)?.currentUserReactionId;

    if (!reactionId) return;

    this.notesService.removeReaction(this.selectedNote.formId, node.id!, reactionId).pipe(
      takeUntilDestroyed(this.destroy),
      catchError(() => of(null))
    ).subscribe(() => {
      if (node.reactions) {
        node.reactions = node.reactions.filter(r => r.id !== reactionId);
      }
      this.updateNodeReactionSummary(node, emoji, false);
      this.cdr.markForCheck();
    });
  }

  toggleReaction(node: IMeetingNote, emoji: string): void {
    const reaction = node.reactionSummary?.find(r => r.emoji === emoji);
    if (reaction?.hasCurrentUserReacted) {
      this.removeReaction(node, emoji);
    } else {
      const emojiItem = this.reactionEmojis.find(e => e.emoji === emoji) || { emoji, code: '', label: '' };
      this.addReaction(node, emojiItem);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.reaction-picker-container')) {
      this.activeReactionPickerNoteId = null;
    }
  }

  // ============================================================
  // Helpers
  // ============================================================

  getInitials(userId: number | null | undefined): string {
    if (userId === 0) return 'SYS';
    if (!userId) return 'U';
    const user = this.users?.find(u => u.id === userId);
    if (user) {
      return `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase();
    }
    return initialsOf(this.authorDirectory.nameById(userId)) || 'U';
  }

  getUserName(userId: number | null | undefined): string {
    if (userId === 0) return 'System';
    if (userId === null || userId === undefined) return 'Unknown';
    const user = this.users?.find(u => u.id === userId);
    const name = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
    return name || this.authorDirectory.nameById(userId) || 'Unknown';
  }

  isOwnMessage(userId: number | null | undefined): boolean {
    return userId === this.currentUser?.userId;
  }

  getCompanyName(companyId: number | undefined): string {
    if (!companyId) return '';
    const company = this.companies?.find((c: any) => c.id === companyId);
    return company?.name || '';
  }

  getParentAuthorName(parentNoteId: number | null | undefined): string {
    if (!parentNoteId) return 'Unknown';
    const parentNote = this.findNoteById(parentNoteId);
    if (parentNote) {
      return this.getUserName(parentNote.userID);
    }
    return 'Unknown';
  }

  getReactionTooltip(reaction: IReactionSummary): string {
    if (!reaction.userAzureObjectIds?.length) return reaction.emoji;
    const names = reaction.userAzureObjectIds.slice(0, 5).map(azureId => {
      const user = this.users?.find(u => u.azureObjectId === azureId);
      return (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '')
        || this.authorDirectory.nameByKey(azureId) || 'Unknown';
    });
    if (reaction.userAzureObjectIds.length > 5) {
      names.push(`and ${reaction.userAzureObjectIds.length - 5} more`);
    }
    return names.join(', ');
  }

  formatTimeAgo(dateStr: string): string {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  }

  formatDate(dateStr: string | number): string {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
    } catch {
      return String(dateStr);
    }
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'insert_drive_file';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'picture_as_pdf';
      case 'doc': case 'docx': return 'description';
      case 'xls': case 'xlsx': return 'table_chart';
      case 'zip': case 'rar': return 'folder_zip';
      case 'png': case 'jpg': case 'jpeg': case 'gif': return 'image';
      default: return 'insert_drive_file';
    }
  }

  getFileIconClass(fileName: string): string {
    if (!fileName) return 'default';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'pdf';
      case 'doc': case 'docx': return 'doc';
      case 'xls': case 'xlsx': return 'xls';
      default: return 'default';
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

/** Activity rows identify the actor in one of three ways depending on their source. */
  getActivityActor(activity: INoteActivity): string {
    if (activity.userDisplayName) return activity.userDisplayName;
    if (activity.userAzureObjectId) {
      const user = this.users?.find(u => u.azureObjectId === activity.userAzureObjectId);
      if (user) return (user.firstName + ' ' + user.lastName).trim();
      const former = this.authorDirectory.nameByKey(activity.userAzureObjectId);
      if (former) return former;
    }
    return this.getUserName(activity.userId);
  }

  getActivityIcon(type: string): string {
    switch (type) {
      case 'reply': return 'chat_bubble_outline';
      case 'upload': return 'attach_file';
      case 'reaction': return 'emoji_emotions';
      case 'status_change': return 'swap_horiz';
      case 'created': return 'add_circle_outline';
      case 'mention': return 'alternate_email';
      case 'edit': return 'edit';
      case 'deleted': return 'delete_outline';
      case 'time': return 'schedule';
      default: return 'info';
    }
  }

  titleHasYear(note: any): boolean {
    return note?.year && note?.title?.includes(String(note.year));
  }

  stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  }

  isSystemMessage(note: IMeetingNote): boolean {
    return note.userID === 0 || note.userID === null || note.userID === undefined;
  }

  get discussionNotes(): IMeetingNote[] {
    return this.allNotesFlat.filter(n => !this.isSystemMessage(n) || isConversationSystemMessage(n));
  }

  get systemNotes(): IMeetingNote[] {
    return this.allNotesFlat.filter(n => this.isSystemMessage(n) && !isConversationSystemMessage(n));
  }

  trackByNoteId(index: number, item: IMeetingNote): number | undefined {
    return item.id;
  }

  trackByListItem(index: number, item: ITaggedNoteListItem): number {
    return item.noteId;
  }

  /** The list in Outlook-style sections, as on the Notes page: Favourites pinned, then Today, Yesterday… */
  get noteGroups(): NoteDateGroup[] {
    return groupNotesByDate(this.taggedNotes);
  }

  readonly groupCollapse = new NoteGroupCollapseState();

  toggleGroup(key: string): void {
    this.groupCollapse.toggle(key);
    this.cdr.markForCheck();
  }

  trackByGroup(index: number, group: NoteDateGroup): string {
    return group.key;
  }

  // ============================================================
  // Timer
  // ============================================================

  private loadFormCustomerInfo(formId: number): void {
    if (!formId) return;
    this.formsService.getFormToEdit(1, formId).pipe(
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroy)
    ).subscribe((formData: any) => {
      if (formData) {
        this.formCustomerInfo = {
          businessId: formData.businessId,
          individualId: formData.individualId,
          formType: formData.formType,
        };
        // Same rule: this customer belongs to the form on screen, which is not necessarily the form
        // the running timer will be saved against.
        if (this.isTimerRunning && this.timerOwner?.context.formId === formId) {
          this.timerOwner = {
            ...this.timerOwner,
            context: {
              ...this.timerOwner.context,
              businessId: formData.businessId ?? null,
              individualId: formData.individualId ?? null,
            },
          };
          this.persistTimer();
        }
      }
    });
  }

  private get timerKey(): string {
    return this.selectedNote ? this.keyFor(this.selectedNote) : '';
  }

  private keyFor(note: ITaggedNoteListItem): string {
    return `note:${note.formId}:${note.rootMeetingNoteId ?? note.noteId}`;
  }

  /**
   * The note a running timer belongs to, fixed the moment it starts.
   *
   * Everything used to be derived from the this.selectedNote note at the time of writing, so opening another
   * note while a timer ran re-saved the same start time under the new note's key. One phantom timer
   * per note visited, every one of them showing the original's elapsed time, and Stop clearing only
   * the one you happened to be looking at -- the rest came back on the next visit.
   */
  private timerOwner: {
    key: string;
    label: string;
    reference: string;
    returnUrl: string;
    context: TimerContext;
  } | null = null;

  /** Snapshot of the selected note, taken when a timer starts on it. */
  private buildTimerOwner(): typeof this.timerOwner {
    if (!this.selectedNote) return null;

    const formId = this.selectedNote.formId;
    const noteId = this.selectedNote.rootMeetingNoteId ?? this.selectedNote.noteId;

    return {
      key: this.keyFor(this.selectedNote),
      label: this.noteThread?.title || this.selectedNote.title || 'Meeting Note',
      reference: this.noteThread?.referenceNumber || this.selectedNote.referenceNumber || '',
      returnUrl: `/tagged-notes?noteId=${noteId}&formId=${formId}`,
      context: {
        formId,
        noteId,
        departmentId: this.noteThread?.departmentId || this.selectedNote.departmentId,
        serviceId: this.noteThread?.serviceId || this.selectedNote.serviceId,
        businessId: this.formCustomerInfo?.businessId ?? null,
        individualId: this.formCustomerInfo?.individualId ?? null,
        year: this.selectedNote.year || new Date().getFullYear(),
        title: this.noteThread?.title || this.selectedNote.title || 'Meeting Note',
        referenceNumber: this.noteThread?.referenceNumber || this.selectedNote.referenceNumber || '',
      },
    };
  }

  /**
   * The header timer shows the this.selectedNote note's timer and nothing else. A timer running on another
   * note is left alone -- it keeps running, stays in the store and stays in the global widget -- but
   * it is detached from this view, so it cannot be stopped, described, or written to a timesheet
   * against a note it does not belong to.
   */
  private syncTimerToSelectedNote(): void {
    if (this.timerOwner && this.timerOwner.key !== this.timerKey) this.detachTimer();
    this.restoreTimer();
  }


  /**
   * Watches the store for the timer this view is showing. It can be stopped and saved from the
   * global widget while the note sits open here, and until this existed nothing said so: the header
   * kept counting a timer that no longer existed, and the next persist brought it back.
   */
  private watchTimerStore(): void {
    this.timerStore.timers$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(timers => {
      const owner = this.timerOwner;
      if (!owner || timers.some(t => t.key === owner.key)) return;

      this.detachTimer();
      this.cdr.markForCheck();
    });
  }

  /** Lets go of a running timer without stopping it. */
  private detachTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.isTimerRunning = false;
    this.timerStartTime = null;
    this.timerElapsedSeconds = 0;
    this.timerDescription = '';
    this.showTimerDescription = false;
    this.timerOwner = null;
  }

  private startTimerInterval(): void {
    this.timerInterval = setInterval(() => {
      if (this.timerStartTime) {
        this.timerElapsedSeconds = Math.floor((Date.now() - this.timerStartTime.getTime()) / 1000);
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  /**
   * Billable belongs to the timer, not just to this screen. The template used to flip the field
   * directly, so the store kept whatever the flag was when the timer started: stopping from the
   * global widget -- which seeds its dialog from the stored value -- offered "Billable" for a timer
   * that had been marked non-billable here, and a reload restored it the same way.
   */
  toggleBillable(): void {
    this.timerBillable = !this.timerBillable;
    if (this.isTimerRunning) this.persistTimer();
  }

  private persistTimer(): void {
    if (!this.timerStartTime || !this.timerOwner) return;

    this.timerStore.save({
      key: this.timerOwner.key,
      startTimeIso: this.timerStartTime.toISOString(),
      billable: this.timerBillable,
      description: this.timerDescription,
      showDescription: this.showTimerDescription,
      kind: 'note',
      label: this.timerOwner.label,
      reference: this.timerOwner.reference,
      returnUrl: this.timerOwner.returnUrl,
      context: this.timerOwner.context,
    });
  }

  /** Resume a timer that was running for this note before a reload / re-login / navigation. */
  private restoreTimer(): void {
    if (this.isTimerRunning) return;
    const key = this.timerKey;
    const saved = this.timerStore.load(key);
    if (!saved) return;
    this.timerOwner = {
      key,
      label: saved.label || '',
      reference: saved.reference || '',
      returnUrl: saved.returnUrl || '',
      context: saved.context || {},
    };
    this.timerStartTime = new Date(saved.startTimeIso);
    this.timerBillable = saved.billable;
    this.timerDescription = saved.description || '';
    this.showTimerDescription = !!saved.showDescription;
    this.isTimerRunning = true;
    this.timerElapsedSeconds = Math.floor((Date.now() - this.timerStartTime.getTime()) / 1000);
    this.startTimerInterval();
    this.cdr.markForCheck();
  }

  startTimer(): void {
    if (this.isTimerRunning) return;
    this.timerOwner = this.buildTimerOwner();
    if (!this.timerOwner) return;
    this.isTimerRunning = true;
    this.timerStartTime = new Date();
    this.timerElapsedSeconds = 0;
    this.timerDescription = '';
    this.showTimerDescription = false;
    this.persistTimer();
    this.startTimerInterval();
  }

  /** True when the note itself supplies a department + service, so the inline picker isn't needed. */
  get noteHasDeptService(): boolean {
    const dept = this.noteThread?.departmentId || this.selectedNote?.departmentId;
    const svc = this.noteThread?.serviceId || this.selectedNote?.serviceId;
    return !!(dept && svc);
  }

  /** Services available for the department chosen in the inline timer picker. */
  get timerServices(): { id: number; description: string }[] {
    const dept = this.timerDepartments.find(d => d.id === this.timerDepartmentId);
    return (dept?.services || []).map(s => ({ id: s.id!, description: s.description! }))
      .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''));
  }

  onTimerDepartmentChange(): void {
    this.timerServiceId = null;
  }

  private loadTimerDepartments(): void {
    if (this.timerDepartments.length) return;
    this.companiesService.getDepartments(true).subscribe({
      next: (departments) => { this.timerDepartments = [...(departments || [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')); this.cdr.markForCheck(); },
      error: (err) => console.error('Error loading departments for timer:', err),
    });
  }

  stopTimer(): void {
    if (!this.isTimerRunning) return;

    if (!this.showTimerDescription) {
      this.showTimerDescription = true;
      this.timerDescription = '';
      if (!this.noteHasDeptService) this.loadTimerDepartments();
      this.persistTimer();
      this.cdr.markForCheck();
      return;
    }

    // Validate BEFORE tearing down so elapsed time is never lost on failure.
    if (!this.formCustomerInfo || (!this.formCustomerInfo.businessId && !this.formCustomerInfo.individualId)) {
      this.snackBar.open('Cannot create timesheet: customer info not available yet. Timer kept running.', 'Close', { duration: 5000 });
      return;
    }

    const departmentId = this.noteThread?.departmentId || this.selectedNote?.departmentId || this.timerDepartmentId;
    const serviceId = this.noteThread?.serviceId || this.selectedNote?.serviceId || this.timerServiceId;

    if (!departmentId || !serviceId) {
      this.snackBar.open('Select a department and service for this timesheet entry. Timer kept running.', 'Close', { duration: 5000 });
      return;
    }

    const startTime = this.timerStartTime!;
    const endTime = new Date();
    this.isTimerRunning = false;
    clearInterval(this.timerInterval);
    this.timerInterval = null;
    this.showTimerDescription = false;

    this.createTimesheetEntry(startTime, endTime, departmentId, serviceId);
  }

  cancelTimer(): void {
    this.isTimerRunning = false;
    clearInterval(this.timerInterval);
    this.timerInterval = null;
    this.showTimerDescription = false;
    this.resetTimer();
    this.cdr.markForCheck();
  }

  /** Re-arm the timer after a failed save so the user can retry without losing time. */
  private resumeTimerAfterFailure(): void {
    if (this.isTimerRunning || !this.timerStartTime) return;
    this.isTimerRunning = true;
    this.persistTimer();
    this.startTimerInterval();
    this.cdr.markForCheck();
  }

  private createTimesheetEntry(startTime: Date, endTime: Date, departmentId: number, serviceId: number): void {
    const meetingNoteUrl = this.generateTimerMeetingNoteUrl();
    const userComment = this.timerDescription?.trim() || '';
    const autoComment = `Meeting Note: ${this.noteThread?.referenceNumber || ''} | ${meetingNoteUrl}`;
    const comments = userComment ? `${userComment} | ${autoComment}` : autoComment;

    const timeSheetRequest: ITimeSheetRequest = {
      date: formatDate(startTime, 'yyyy-MM-dd', 'en-US'),
      serviceId: serviceId,
      departmentId: departmentId,
      individualId: this.formCustomerInfo?.individualId || null,
      businessId: this.formCustomerInfo?.businessId || null,
      organizationCompanyId: null,
      description: this.noteThread?.title || 'Meeting Note',
      startTime: this.formatTimeForApi(startTime),
      endTime: this.formatTimeForApi(endTime),
      billable: this.timerBillable,
      comments: comments,
      referenceNumber: this.noteThread?.referenceNumber || this.selectedNote?.referenceNumber || undefined,
      year: new Date().getFullYear(),
      attendees: [],
    } as any;

    this.timeSheetService.createTimerEntries(timeSheetRequest, startTime, endTime).pipe(
      takeUntilDestroyed(this.destroy),
      catchError((err) => {
        this.snackBar.open('Error creating timesheet entry. Timer resumed — try again.', 'Close', { duration: 5000 });
        this.resumeTimerAfterFailure();
        throw err;
      })
    ).subscribe(() => {
      this.snackBar.open(`Timesheet entry created: ${this.formatElapsedTime(this.timerElapsedSeconds)}`, 'Close', { duration: 5000 });
      this.resetTimer();
      this.timerDepartmentId = null;
      this.timerServiceId = null;
      // Refresh the Timesheet tab if it's currently open.
      this.timesheetList?.reload();
      this.cdr.markForCheck();
    });
  }

  private generateTimerMeetingNoteUrl(): string {
    if (!this.selectedNote) return '';
    const baseUrl = window.location.origin;
    const noteUrl = this.router.createUrlTree(['/tagged-notes'], {
      queryParams: {
        noteId: this.selectedNote.rootMeetingNoteId,
        formId: this.selectedNote.formId,
      }
    });
    return `${baseUrl}${this.router.serializeUrl(noteUrl)}`;
  }

  private resetTimer(): void {
    const key = this.timerOwner?.key || this.timerKey;
    this.timerOwner = null;
    this.timerStore.clear(key);
    this.timerElapsedSeconds = 0;
    this.timerStartTime = null;
    this.timerDescription = '';
  }

  private formatTimeForApi(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  formatElapsedTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // ============================================================
  // Tree Helpers
  // ============================================================

  private flattenNotes(notes: IMeetingNote[]): void {
    this.allNotesFlat = [];
    this.flattenNotesInto(notes, this.allNotesFlat);
    this.sortForChat(this.allNotesFlat);

    // Compute unique participant IDs from the thread
    const userIds = new Set<number>();
    for (const note of this.allNotesFlat) {
      if (note.userID !== null && note.userID !== undefined) {
        userIds.add(note.userID);
      }
    }
    this.threadParticipantIds = Array.from(userIds).filter(id => id !== 0);
  }


  /**
   * Chat order, not tree order.
   *
   * The flatten walks the reply tree, so a reply sat directly under the message it answered — a
   * reply written today to something said last week landed in the middle of the conversation,
   * above messages that came before it. A thread reads as a conversation, so it is ordered by when
   * each message was posted; the "Reply to X" line above the bubble carries the threading.
   */
  private sortForChat(notes: IMeetingNote[]): void {
    notes.sort((a, b) => {
      const byTime = this.messageTime(a) - this.messageTime(b);
      return byTime !== 0 ? byTime : (a.id ?? 0) - (b.id ?? 0);
    });
  }

  /** Posted-at as a number. An unparsable date sorts to the top rather than scrambling the list. */
  private messageTime(note: IMeetingNote): number {
    const time = new Date(note.date).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  private flattenNotesInto(notes: IMeetingNote[], result: IMeetingNote[]): void {
    for (const note of notes) {
      result.push(note);
      if (note.replies?.length) {
        this.flattenNotesInto(note.replies, result);
      }
    }
  }

  private findNoteById(noteId: number): IMeetingNote | null {
    const find = (notes: IMeetingNote[]): IMeetingNote | null => {
      for (const note of notes) {
        if (note.id === noteId) return note;
        if (note.replies?.length) {
          const found = find(note.replies);
          if (found) return found;
        }
      }
      return null;
    };
    return this.noteThread ? find([this.noteThread]) : null;
  }

  private updateNoteInTree(noteId: number, newNotes: string): void {
    const update = (notes: IMeetingNote[]): boolean => {
      for (const note of notes) {
        if (note.id === noteId) { note.notes = newNotes; return true; }
        if (note.replies?.length && update(note.replies)) return true;
      }
      return false;
    };
    if (this.noteThread) update([this.noteThread]);
  }

  private addReplyToTree(parentId: number, newReply: IMeetingNote): void {
    const add = (notes: IMeetingNote[]): boolean => {
      for (const note of notes) {
        if (note.id === parentId) {
          if (!note.replies) note.replies = [];
          note.replies.push(newReply);
          return true;
        }
        if (note.replies?.length && add(note.replies)) return true;
      }
      return false;
    };
    if (this.noteThread) add([this.noteThread]);
  }

  private deleteNoteFromTree(noteToDelete: IMeetingNote): void {
    const del = (notes: IMeetingNote[]): boolean => {
      for (let i = 0; i < notes.length; i++) {
        if (notes[i].id === noteToDelete.id) { notes.splice(i, 1); return true; }
        if (notes[i].replies?.length && del(notes[i].replies!)) return true;
      }
      return false;
    };
    if (this.noteThread?.replies) del(this.noteThread.replies);
  }

  private updateNodeReactionSummary(
    node: IMeetingNote, emoji: string, isAdding: boolean, reactionId?: number
  ): void {
    if (!node.reactionSummary) node.reactionSummary = [];

    const idx = node.reactionSummary.findIndex(r => r.emoji === emoji);

    if (isAdding) {
      if (idx >= 0) {
        node.reactionSummary[idx].count++;
        node.reactionSummary[idx].hasCurrentUserReacted = true;
        node.reactionSummary[idx].currentUserReactionId = reactionId;
        if (!node.reactionSummary[idx].userAzureObjectIds.includes(this.currentUser.azureObjectId)) {
          node.reactionSummary[idx].userAzureObjectIds.push(this.currentUser.azureObjectId);
        }
      } else {
        node.reactionSummary.push({
          emoji, count: 1,
          userAzureObjectIds: [this.currentUser.azureObjectId],
          hasCurrentUserReacted: true,
          currentUserReactionId: reactionId,
        });
      }
    } else {
      if (idx >= 0) {
        node.reactionSummary[idx].count--;
        node.reactionSummary[idx].hasCurrentUserReacted = false;
        node.reactionSummary[idx].currentUserReactionId = undefined;
        node.reactionSummary[idx].userAzureObjectIds = node.reactionSummary[idx].userAzureObjectIds.filter(
          id => id !== this.currentUser.azureObjectId
        );
        if (node.reactionSummary[idx].count <= 0) {
          node.reactionSummary.splice(idx, 1);
        }
      }
    }
    node.reactionSummary = [...node.reactionSummary];
  }

  private loadReactionsForNotes(notes: IMeetingNote[]): void {
    if (!this.selectedNote) return;
    const withIds = notes.filter(n => n.id);
    if (!withIds.length) return;

    this.notesService.getReactionsForNotes(this.selectedNote.formId, withIds.map(n => n.id!)).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(byNote => {
      withIds.forEach(note => {
        note.reactions = byNote.get(note.id!) ?? [];
        note.reactionSummary = this.buildReactionSummary(note.reactions);
      });
      this.cdr.markForCheck();
    });
  }

  private buildReactionSummary(reactions: IReaction[]): IReactionSummary[] {
    if (!reactions?.length) return [];
    const map = new Map<string, IReactionSummary>();

    for (const r of reactions) {
      const existing = map.get(r.emoji);
      if (existing) {
        existing.count++;
        existing.userAzureObjectIds.push(r.userAzureObjectId);
        if (r.userAzureObjectId === this.currentUser?.azureObjectId) {
          existing.hasCurrentUserReacted = true;
          existing.currentUserReactionId = r.id;
        }
      } else {
        map.set(r.emoji, {
          emoji: r.emoji, count: 1,
          userAzureObjectIds: [r.userAzureObjectId],
          hasCurrentUserReacted: r.userAzureObjectId === this.currentUser?.azureObjectId,
          currentUserReactionId: r.userAzureObjectId === this.currentUser?.azureObjectId ? r.id : undefined,
        });
      }
    }
    return Array.from(map.values());
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    } catch {}
  }
}
